import { Link } from 'react-router';
import { CalendarDays, ChevronRight, Clock3, Gift, Receipt, ShoppingBag, Sparkles, Tag } from 'lucide-react';
import type { OrderHistoryItem } from '@ulp/consumer-core';
import { DEFAULT_PROFILE } from '@ulp/consumer-core';
import { useAppConfig, useHome, useOrderHistory, useRewardWallet } from '../lib/queries';
import { RESERVATION_STATUS, useReservations, useReservationSettings } from '../lib/reservations';
import { useSession } from '../lib/session';
import { useTenant } from '../lib/tenant';
import { firstName, fmtRemaining, fmtTime, fmtWeekday, greeting } from '../lib/format';
import { Badge, Banner, Button, Card, cx, SectionTitle, Skeleton } from '../ui/kit';
import { MembershipCard, StampCard } from '../ui/loyalty';
import { TenantLogo } from '../ui/brand';
import { InstallHint } from '../ui/install';

const ACTIVE_FULFILLMENT = ['PLACED', 'PREPARING', 'READY'];

export function Home() {
  const { bootstrap, path } = useTenant();
  const { guest, signedIn } = useSession();
  const { data: config } = useAppConfig();
  const home = useHome();
  const orders = useOrderHistory();
  const rewards = useRewardWallet();
  const reservations = useReservations();
  const reservationSettings = useReservationSettings();
  const profile = config?.profile ?? DEFAULT_PROFILE;

  const activeOrder = orders.data?.find((o) => o.fulfillmentStatus && ACTIVE_FULFILLMENT.includes(o.fulfillmentStatus) && o.status !== 'CANCELLED');
  const nextReservation = reservations.data?.upcoming.find((r) => r.status === 'PENDING' || r.status === 'CONFIRMED');
  const availableRewards = rewards.data?.available.length ?? 0;
  const name = firstName(home.data?.member.fullName);

  return (
    <div className="px-4 pt-safe">
      <header className="flex items-center gap-3 px-1 pb-4 pt-5">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium text-muted">{greeting()}</p>
          <h1 className="truncate text-[24px] font-extrabold tracking-[-0.03em]">{name ? `${name} 👋` : bootstrap?.tenant.name ?? 'Hoş geldin'}</h1>
        </div>
        <TenantLogo size={44} />
      </header>

      {guest ? (
        <GuestCard />
      ) : home.data ? (
        <MembershipCard home={home.data} walletEnabled={profile.features.payWithWallet} className="animate-rise" />
      ) : home.isError ? (
        <Banner tone="danger" title="Kart yüklenemedi" action={<Button size="sm" variant="secondary" onClick={() => void home.refetch()}>Yenile</Button>}>
          Bağlantını kontrol edip tekrar dene.
        </Banner>
      ) : (
        <Skeleton className="h-[248px] w-full rounded-[28px]" />
      )}

      {activeOrder && <ActiveOrderCard order={activeOrder} />}
      {nextReservation && (
        <Link to={path(`rezervasyon/${nextReservation.id}`)} className="mt-4 block animate-rise">
          <Card className="flex items-center gap-4 p-4">
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-brand-soft text-brand">
              <CalendarDays className="size-6" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-bold">
                {fmtWeekday(nextReservation.startsAt)} · {fmtTime(nextReservation.startsAt)}
              </p>
              <p className="truncate text-[13px] text-muted">
                {nextReservation.partySize} kişi · {nextReservation.branch.name}
              </p>
            </div>
            <Badge tone={RESERVATION_STATUS[nextReservation.status]?.tone ?? 'neutral'}>{RESERVATION_STATUS[nextReservation.status]?.label}</Badge>
          </Card>
        </Link>
      )}

      <section className="mt-6 grid grid-cols-4 gap-2.5" aria-label="Hızlı işlemler">
        <QuickAction to={path('siparis')} icon={<ShoppingBag className="size-6" />} label={profile.features.mobileOrder ? 'Sipariş' : 'Menü'} />
        {reservationSettings.data?.enabled !== false && <QuickAction to={path('rezervasyon/yeni')} icon={<CalendarDays className="size-6" />} label="Masa ayırt" />}
        <QuickAction to={path('oduller')} icon={<Gift className="size-6" />} label="Ödüller" badge={availableRewards || undefined} />
        <QuickAction to={path('hesap/fis-ekle')} icon={<Receipt className="size-6" />} label="Fiş ekle" />
      </section>

      {signedIn && availableRewards > 0 && (
        <Link to={path('oduller')} className="mt-6 block animate-rise">
          <div className="gold-fill flex items-center gap-4 rounded-3xl p-4 shadow-glow">
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-white/35">
              <Sparkles className="size-6" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[16px] font-extrabold">{availableRewards} ödülün hazır</p>
              <p className="text-[13px] font-medium opacity-80">Kasada karekodunu okut, hemen kullan.</p>
            </div>
            <ChevronRight className="size-5" />
          </div>
        </Link>
      )}

      {signedIn && (home.data?.stampCards.length ?? 0) > 0 && (
        <section className="mt-8">
          <SectionTitle>Damga kartım</SectionTitle>
          <div className="space-y-3">
            {home.data!.stampCards.map((c) => (
              <StampCard key={c.programId} card={c} />
            ))}
          </div>
        </section>
      )}

      {signedIn && (home.data?.offers.length ?? 0) > 0 && (
        <section className="mt-8">
          <SectionTitle>Sana özel</SectionTitle>
          <div className="no-scrollbar -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2">
            {home.data!.offers.map((o, i) => (
              <article
                key={o.id}
                className={cx(
                  'relative w-[78%] shrink-0 snap-start overflow-hidden rounded-3xl p-5 shadow-card',
                  i % 2 === 0 ? 'card-emerald' : 'bg-surface ring-1 ring-line/60',
                )}
              >
                <Tag className={cx('size-6', i % 2 === 0 ? 'text-gold-bright' : 'text-gold')} />
                <p className="mt-4 text-[18px] font-extrabold leading-snug tracking-[-0.02em]">{o.title || o.name}</p>
                {o.description && <p className={cx('mt-1.5 line-clamp-3 text-[13.5px] leading-snug', i % 2 === 0 ? 'text-white/75' : 'text-muted')}>{o.description}</p>}
                {o.endsAt && (
                  <p className={cx('mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold', i % 2 === 0 ? 'text-gold-bright' : 'text-warning')}>
                    <Clock3 className="size-3.5" /> {fmtRemaining(o.endsAt)}
                  </p>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      <InstallHint className="mt-8" />
      <div className="h-6" />
    </div>
  );
}

function QuickAction({ to, icon, label, badge }: { to: string; icon: React.ReactNode; label: string; badge?: number }) {
  return (
    <Link to={to} className="group flex flex-col items-center gap-2 rounded-2xl bg-surface py-3.5 shadow-card ring-1 ring-line/60 transition-transform active:scale-95">
      <span className="relative grid size-11 place-items-center rounded-2xl bg-brand-soft text-brand transition-colors group-active:bg-brand group-active:text-brand-ink">
        {icon}
        {badge ? <span className="gold-fill absolute -right-1.5 -top-1.5 grid min-w-5 place-items-center rounded-full px-1 text-[11px] font-extrabold leading-5 ring-2 ring-surface">{badge}</span> : null}
      </span>
      <span className="text-[12.5px] font-semibold text-ink-2">{label}</span>
    </Link>
  );
}

function GuestCard() {
  const { path } = useTenant();
  return (
    <div className="card-emerald relative animate-rise overflow-hidden rounded-[28px] p-6 shadow-float">
      <div className="pointer-events-none absolute -right-10 -top-16 size-48 rounded-full bg-gold/20 blur-2xl" aria-hidden />
      <p className="text-[13px] font-semibold uppercase tracking-[0.12em] text-gold-bright">Misafir</p>
      <p className="mt-2 text-[22px] font-extrabold leading-tight tracking-[-0.02em]">Üye ol, bu siparişten itibaren yıldız kazan.</p>
      <p className="mt-2 text-[14px] text-white/70">Misafir siparişlerin üyeliğine taşınır.</p>
      <Link to={path('giris')} className="mt-5 inline-block">
        <Button variant="gold">Üye ol</Button>
      </Link>
    </div>
  );
}

const ORDER_STEP: Record<string, string> = { PLACED: 'Sipariş alındı', PREPARING: 'Hazırlanıyor', READY: 'Hazır — teslim alabilirsin' };

function ActiveOrderCard({ order }: { order: OrderHistoryItem }) {
  const { path } = useTenant();
  const ready = order.fulfillmentStatus === 'READY';
  return (
    <Link to={path(`siparis/${order.id}`)} className="mt-4 block animate-rise">
      <Card className={cx('flex items-center gap-4 p-4', ready && 'ring-2 ring-gold')}>
        <span className={cx('relative grid size-12 shrink-0 place-items-center rounded-2xl', ready ? 'gold-fill' : 'bg-brand-soft text-brand')}>
          <ShoppingBag className="size-6" />
          {!ready && <span className="absolute -right-0.5 -top-0.5 size-3 animate-ping rounded-full bg-brand/60" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold">{ORDER_STEP[order.fulfillmentStatus ?? ''] ?? 'Siparişin'}</p>
          <p className="truncate text-[13px] text-muted">
            {order.pickupCode ? `No ${order.pickupCode} · ` : ''}
            {order.items.map((i) => `${i.quantity}× ${i.name}`).join(', ')}
          </p>
        </div>
        <ChevronRight className="size-5 text-muted" />
      </Card>
    </Link>
  );
}
